import { exerciseTypeCatalog, type ExerciseTypeOption } from "./exercise-types";

export type ExerciseTypeTier = "umbrella" | "primary" | "secondary";

export type ExerciseTypeRoles = {
  training_category?: string;
  primary?: string;
  secondary?: string[];
};

const UMBRELLA_IDS = new Set(
  exerciseTypeCatalog.filter((t) => t.tier === "umbrella").map((t) => t.id)
);

const PRIMARY_IDS = new Set(
  exerciseTypeCatalog.filter((t) => t.tier === "primary" || t.tier === "secondary").map((t) => t.id)
);

export function umbrellaExerciseTypeOptions(): ExerciseTypeOption[] {
  return exerciseTypeCatalog.filter((t) => t.tier === "umbrella");
}

export function primaryExerciseTypeOptions(): ExerciseTypeOption[] {
  return exerciseTypeCatalog.filter((t) => t.tier === "primary" || t.tier === "secondary");
}

/** Sync flat exercise_types array from hierarchical roles. */
export function syncExerciseTypesFromRoles(roles: ExerciseTypeRoles): string[] {
  const out: string[] = [];
  const add = (id: string | undefined) => {
    if (id && id.trim() && !out.includes(id)) out.push(id);
  };
  add(roles.training_category);
  add(roles.primary);
  for (const id of roles.secondary ?? []) {
    add(id);
  }
  return out;
}

/** Infer roles from legacy flat exercise_types when roles JSON is empty. */
export function inferRolesFromExerciseTypes(exerciseTypes: string[]): ExerciseTypeRoles {
  const roles: ExerciseTypeRoles = { secondary: [] };
  const remaining = [...exerciseTypes];

  const takeFirst = (predicate: (id: string) => boolean): string | undefined => {
    const idx = remaining.findIndex(predicate);
    if (idx === -1) return undefined;
    return remaining.splice(idx, 1)[0];
  };

  roles.training_category = takeFirst((id) => UMBRELLA_IDS.has(id));
  roles.primary = takeFirst((id) => PRIMARY_IDS.has(id));
  roles.secondary = remaining.filter((id) => PRIMARY_IDS.has(id));
  if (roles.secondary.length === 0) {
    delete roles.secondary;
  }
  return roles;
}

/** Normalize roles for API payload (drop empty fields). */
export function normalizeExerciseTypeRoles(roles: ExerciseTypeRoles): ExerciseTypeRoles {
  const out: ExerciseTypeRoles = {};
  if (roles.training_category?.trim()) {
    out.training_category = roles.training_category.trim();
  }
  if (roles.primary?.trim()) {
    out.primary = roles.primary.trim();
  }
  const secondary = (roles.secondary ?? []).map((s) => s.trim()).filter(Boolean);
  if (secondary.length > 0) {
    out.secondary = [...new Set(secondary)];
  }
  return out;
}

/** Load roles for edit: use stored roles or infer from flat types. */
export function resolveExerciseTypeRoles(
  storedRoles: ExerciseTypeRoles | null | undefined,
  exerciseTypes: string[]
): ExerciseTypeRoles {
  const hasStored =
    storedRoles &&
    (storedRoles.training_category || storedRoles.primary || (storedRoles.secondary?.length ?? 0) > 0);
  if (hasStored) {
    return normalizeExerciseTypeRoles(storedRoles);
  }
  return inferRolesFromExerciseTypes(exerciseTypes);
}
